/* Helpers: dates, storage, formatting, downloads. */

// ----------------------------------------------------------------- storage
const PREFIX = "bdai:";

export function load(key, fallback) {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    return raw === null ? fallback : JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
}

export function save(key, value) {
  try {
    if (value === undefined || value === null) localStorage.removeItem(PREFIX + key);
    else localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch (e) {
    /* private mode / storage full - ignore */
  }
}

// ----------------------------------------------------------------- dates
const pad = (n) => String(n).padStart(2, "0");
export const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function startOfWeek(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = (x.getDay() + 6) % 7; // Monday = 0
  return addDays(x, -day);
}

export function datePresets(today = new Date()) {
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const y = t.getFullYear();
  const m = t.getMonth();
  const q = Math.floor(m / 3);
  const fyStartYear = m >= 3 ? y : y - 1; // Indian FY: 1 April - 31 March
  const wk = startOfWeek(t);
  return [
    { key: "today", label: "Today", from: iso(t), to: iso(t) },
    { key: "yesterday", label: "Yesterday", from: iso(addDays(t, -1)), to: iso(addDays(t, -1)) },
    { key: "this_week", label: "This week", from: iso(wk), to: iso(t) },
    { key: "last_week", label: "Last week", from: iso(addDays(wk, -7)), to: iso(addDays(wk, -1)) },
    { key: "last_7", label: "Last 7 days", from: iso(addDays(t, -6)), to: iso(t) },
    { key: "last_30", label: "Last 30 days", from: iso(addDays(t, -29)), to: iso(t) },
    { key: "this_month", label: "This month", from: iso(new Date(y, m, 1)), to: iso(t) },
    { key: "last_month", label: "Last month", from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) },
    { key: "this_quarter", label: "This quarter", from: iso(new Date(y, q * 3, 1)), to: iso(t) },
    { key: "last_quarter", label: "Last quarter", from: iso(new Date(y, q * 3 - 3, 1)), to: iso(new Date(y, q * 3, 0)) },
    { key: "this_fy", label: "This FY", from: iso(new Date(fyStartYear, 3, 1)), to: iso(t) },
    { key: "last_fy", label: "Last FY", from: iso(new Date(fyStartYear - 1, 3, 1)), to: iso(new Date(fyStartYear, 2, 31)) },
  ];
}

export function formatDate(isoStr) {
  if (!isoStr) return "";
  const [yy, mm, dd] = isoStr.split("-").map(Number);
  if (!yy || !mm || !dd) return isoStr;
  return new Date(yy, mm - 1, dd).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export function rangeLabel(range) {
  if (!range || (!range.from && !range.to)) return "All time";
  if (range.label && range.label !== "Custom") return range.label;
  if (range.from === range.to) return formatDate(range.from);
  return `${range.from ? formatDate(range.from) : "Start"} – ${range.to ? formatDate(range.to) : "Today"}`;
}

export function formatTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}

export function historyGroup(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "Older";
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const diff = (start - new Date(d.getFullYear(), d.getMonth(), d.getDate())) / 86400000;
  if (diff <= 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return "Previous 7 days";
  if (diff < 30) return "Previous 30 days";
  return "Older";
}

// ----------------------------------------------------------------- formatting
export function prettyHeader(key) {
  return String(key)
    .replace(/^(sum|avg|min|max)_/, (m) => `${m.slice(0, -1).toUpperCase()} `)
    .replace(/_/g, " ")
    .replace(/:/g, " · ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const AMOUNT_HINT = /(amount|rupees|emd|fee|cost|value|bid)/i;

export function formatNumber(value, key = "") {
  if (typeof value !== "number" || !Number.isFinite(value)) return value;
  const opts = Number.isInteger(value) ? {} : { maximumFractionDigits: 2 };
  const text = value.toLocaleString("en-IN", opts);
  return AMOUNT_HINT.test(key) && Math.abs(value) >= 1000 ? `₹${text}` : text;
}

export function cellText(value) {
  if (value === null || value === undefined || value === "") return "";
  if (Array.isArray(value)) return value.map(cellText).join(", ");
  if (typeof value === "object") return Object.entries(value).map(([k, v]) => `${k}: ${cellText(v)}`).join("; ");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

export function columnsOf(rows) {
  const cols = [];
  rows.forEach((r) => Object.keys(r || {}).forEach((k) => !cols.includes(k) && cols.push(k)));
  return cols;
}

// ----------------------------------------------------------------- downloads / clipboard
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function tableToCsv(rows) {
  const cols = columnsOf(rows);
  const esc = (v) => {
    const s = cellText(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [cols.map(prettyHeader).map(esc).join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
}

export function tableToTsv(rows) {
  const cols = columnsOf(rows);
  return [cols.map(prettyHeader).join("\t"), ...rows.map((r) => cols.map((c) => cellText(r[c]).replace(/\t|\n/g, " ")).join("\t"))].join("\n");
}

export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch (err) {
      ok = false;
    }
    ta.remove();
    return ok;
  }
}

export function slug(text) {
  return String(text || "table").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 50) || "table";
}

let counter = 0;
export const uid = () => `m${Date.now().toString(36)}${(counter++).toString(36)}`;
